import { useSelector, useDispatch } from "react-redux";
import { chatActions, trackingService } from "../../../di/di";
import { useState, useEffect, useRef } from "react";
import ChatOngoingVideoCallComponent from "./chat_ongoing_video_call_comp";
import Modal from "./tavus/video_call/components/ui/Modal";
import { Camera, Mic, AlertCircle } from "lucide-react";
import { useMediaPermissions } from "../hooks/useMediaPermissions";


const ChatVideoCallComponent = () => {
  const dispatch = useDispatch();
  const [callStage, setCallStage] = useState(0);
  const { permissionState, error: permissionError, checkPermissions } = useMediaPermissions();
  const showVideoCall = useSelector((state) => state.chat.showVideoCall);
  const videoCallData = useSelector((state) => state.chat.videoCallData);
  const currentStage = useSelector((state) => state.chat.currentStage);
  const videoCallUrl = useSelector((state) => state.chat.videoCallUrl);
  const [selectedMode, setSelectedMode] = useState(null);
  const isCallActive = useRef(false);
  const isComponentMounted = useRef(true);
  const callStageRef = useRef(0);
  const ongoingCallRef = useRef(null);
  const callToneAudioRef = useRef(null);
  const mediaStreamsRef = useRef([]);
  const user = useSelector((state) => state.user.user);
  // Update callStageRef when callStage changes
  useEffect(() => {
    callStageRef.current = callStage;
    console.log("callStage updated:", callStage);
    if (callStage === 1) {
      if (callToneAudioRef.current && typeof callToneAudioRef.current.pause === "function") {
        callToneAudioRef.current.pause();
      }
    }
  }, [callStage]);

  // Update callStage when currentStage changes
  useEffect(() => {
    if (callStage !== currentStage) {
      console.log("currentStage changed:", currentStage);
      setCallStage(currentStage);
    }
  }, [currentStage, callStage]);

  // Check if there are any active media tracks in the browser and clean them up
  const forceReleaseAllMediaDevices = () => {
    console.log("Force releasing all media devices");

    // Access the browser's MediaDevices API
    if (!navigator.mediaDevices) {
      console.log("MediaDevices API not available");
      return;
    }

    // Create an emergency cleanup function that gets all tracks
    const emergencyCleanup = () => {
      try {
        // This is a last resort attempt to force the browser to release camera resources
        navigator.mediaDevices
          .getUserMedia({ audio: true, video: true })
          .then((stream) => {
            console.log("Got emergency access to media devices");
            stream.getTracks().forEach((track) => {
              console.log(`Emergency stopping ${track.kind} track`);
              track.enabled = false;
              track.stop();
            });

            // Also try to enumerate to refresh device state
            navigator.mediaDevices
              .enumerateDevices()
              .then(() => console.log("Devices enumerated after emergency stop"))
              .catch((e) => console.error("Enumeration error:", e));
          })
          .catch((e) => {
            console.log("Could not perform emergency cleanup:", e);

            // Last-ditch effort - try with just video
            navigator.mediaDevices
              .getUserMedia({ video: true })
              .then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
              })
              .catch(() => {
                // Try with just audio
                navigator.mediaDevices
                  .getUserMedia({ audio: true })
                  .then((stream) => {
                    stream.getTracks().forEach((track) => track.stop());
                  })
                  .catch((e) => console.log("All emergency cleanups failed"));
              });
          });
      } catch (e) {
        console.error("Fatal error in emergency cleanup:", e);
      }
    };

    // Attempt emergency cleanup
    emergencyCleanup();
  };

  // Component unmount handler with forced cleanup
  useEffect(() => {
    // Set component as mounted
    isComponentMounted.current = true;

    // Handle window focus/blur events to prevent unmounting
    const handleWindowFocus = () => {
      console.log("Window focused, maintaining call connection");
    };

    const handleWindowBlur = () => {
      console.log("Window blurred, maintaining call connection");
    };

    // Add window focus/blur event listeners
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);

    // Handle visibility change to prevent unmounting during minimize/maximize
    const handleVisibilityChange = () => {
      // Do nothing on visibility change to prevent unmounting
      console.log("Visibility changed, maintaining call connection");
    };

    // Add visibility change event listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up when the component unmounts
    return () => {
      console.log("Component unmounting - performing final cleanup");
      isComponentMounted.current = false;

      // Clean up event listeners
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      // Ensure all media streams are stopped when component unmounts
      cleanupMediaResources();

      // Force release as final step
      forceReleaseAllMediaDevices();
    };
  }, []);

  useEffect(() => {
    if (typeof videoCallData === "object") {
      dispatch(chatActions.setVideoCallUrl(videoCallData.conversation_url));
    }
  }, [videoCallData]);

  useEffect(() => {
    if (showVideoCall) {
      setCallStage(0);
      if (callToneAudioRef.current && typeof callToneAudioRef.current.play === "function") {
        callToneAudioRef.current.play().catch((error) => {
          console.error("Error playing call tone:", error);
        });
      }
      // Check permissions when video call is shown
      checkPermissions();
    }

    // When call is closed from outside this component
    if (!showVideoCall && isCallActive.current) {
      console.log("Call closed externally - cleaning up resources");
      isCallActive.current = false;
      cleanupMediaResources();
      forceReleaseAllMediaDevices();
    }
  }, [showVideoCall]);

  // Check permissions immediately when component mounts or showVideoCall changes
  useEffect(() => {
    console.log("Permission state:", permissionState);
    if (showVideoCall) {
      // Only check permissions if they haven't been granted yet
      if (permissionState.camera === "prompt" || permissionState.microphone === "prompt") {
        checkPermissions();
      } else if (permissionState.camera === "denied" || permissionState.microphone === "denied") {
        // Show error for 3 seconds then close
        const timer = setTimeout(() => {
          closeCall();
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [showVideoCall, permissionState]);

  // Track all media streams for cleanup
  const trackMediaStream = (stream) => {
    if (stream) {
      mediaStreamsRef.current.push(stream);
    }
  };

  // Comprehensive cleanup of all media resources
  const cleanupMediaResources = () => {
    console.log("Performing comprehensive media resource cleanup");

    // Clean up all tracked media streams
    if (mediaStreamsRef.current.length > 0) {
      console.log(`Cleaning up ${mediaStreamsRef.current.length} tracked media streams`);
      mediaStreamsRef.current.forEach((stream) => {
        if (stream && typeof stream.getTracks === "function") {
          const tracks = stream.getTracks();
          tracks.forEach((track) => {
            console.log(`Stopping ${track.kind} track with ID: ${track.id}, state: ${track.readyState}`);
            try {
              track.enabled = false; // Disable the track first
              track.stop(); // Then stop it
            } catch (e) {
              console.error(`Error stopping track: ${e.message}`);
            }
          });
        }
      });
      // Clear the streams array
      mediaStreamsRef.current = [];
    } else {
      console.log("No tracked media streams to clean up");
    }

    // Stop all active MediaStreamTracks from the current browser tab
    try {
      // Get all media devices directly from the browser
      if (typeof navigator !== "undefined" && navigator.mediaDevices) {
        console.log("Attempting to stop all media tracks from the current browser context");

        // For all browser compatibility
        const mediaDevices = navigator.mediaDevices;
        if (mediaDevices.getTracks) {
          const allTracks = mediaDevices.getTracks();
          allTracks.forEach((track) => {
            console.log(`Stopping global track: ${track.kind}`);
            track.stop();
          });
        }

        // Specific browser implementations
        if (navigator.getUserMedia) {
          navigator.getUserMedia(
            { audio: false, video: false },
            () => {},
            () => {}
          );
        }

        // Enumerate devices to refresh state
        mediaDevices
          .enumerateDevices()
          .then(() => {
            console.log("Enumerated devices after cleanup");
          })
          .catch((err) => {
            console.error("Error enumerating devices:", err);
          });
      }
    } catch (e) {
      console.error("Error during global media cleanup:", e);
    }

    // Force cleanup of existing references
    if (ongoingCallRef.current) {
      console.log("Cleaning up ongoing call reference");
      if (typeof ongoingCallRef.current.forceCleanup === "function") {
        try {
          ongoingCallRef.current.forceCleanup();
        } catch (e) {
          console.error("Error during forceCleanup:", e);
        }
      }
      ongoingCallRef.current = null;
    }

    // Additional browser-specific cleanup for Safari and Firefox
    if (window.RTCPeerConnection) {
      const emptyConstraints = { audio: false, video: false };
      navigator.mediaDevices
        .getUserMedia(emptyConstraints)
        .then((dummyStream) => {
          dummyStream.getTracks().forEach((track) => track.stop());
          console.log("Released media resources via dummy stream");
        })
        .catch((err) => {
          console.log("No additional cleanup needed:", err);
        });
    }
  };

  const closeCall = () => {
    console.log("Close call initiated from UI");
    // console.log("Tracking event:", user);
    // trackingService.trackEvent("video_call_user_left", { userId: user.id, distinct_id: user.id, timestamp: new Date().toISOString() });
    // Set flags to indicate call is closing
    isCallActive.current = false;
    callStageRef.current = -1;

    // 1. Immediate state change to ensure UI updates
    dispatch(chatActions.closeVideoCall());
    setCallStage(0);

    // 2. Ensure camera/mic are released immediately
    try {
      if (ongoingCallRef.current && ongoingCallRef.current.forceCleanup) {
        console.log("Calling forceCleanup on ongoingCallRef");
        ongoingCallRef.current.forceCleanup();
      }
    } catch (e) {
      console.error("Error during ongoingCallRef cleanup:", e);
    }

    // 3. Perform comprehensive media resource cleanup
    cleanupMediaResources();

    // 4. Additional forced cleanup after a short delay (safety measure)
    setTimeout(() => {
      if (!isComponentMounted.current) return;
      console.log("Performing delayed cleanup check");
      cleanupMediaResources();

      // Handle any specific browser that might not have released the camera
      if (typeof navigator !== "undefined" && navigator.mediaDevices) {
        navigator.mediaDevices
          .getUserMedia({ audio: false, video: false })
          .then((stream) => {
            stream.getTracks().forEach((track) => {
              track.stop();
            });
          })
          .catch((e) => {
            console.log("No more streams to clean up in delayed check");
          });
      }
    }, 300);
  };

  // Heart icon
  const IconHeart = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" stroke="none" fill="currentColor">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  );

  // Close icon
  const IconClose = () => (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  // Don't render anything if permissions are denied
  if (showVideoCall && (permissionState.camera === "denied" || permissionState.microphone === "denied")) {
    return (
      <div className="absolute inset-0 w-full h-full bg-slate-900/50 backdrop-blur-md flex items-center justify-center" style={{ zIndex: 50 }}>
        <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              {permissionState.camera === "denied" ? <Camera className="w-8 h-8 text-red-500" /> : <Mic className="w-8 h-8 text-red-500" />}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Permission Denied</h3>
            <p className="text-slate-300 mb-6">
              {permissionState.camera === "denied"
                ? "Camera access is required for video calls. Please enable it in your browser settings."
                : "Microphone access is required for video calls. Please enable it in your browser settings."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!showVideoCall) {
    return null;
  }

  // Use callStageRef.current instead of callStage to prevent re-renders
  if (showVideoCall && callStage === 1) {
    // Set call as active when rendering the ongoing call component
    isCallActive.current = true;

    return (
      <ChatOngoingVideoCallComponent
        ref={ongoingCallRef}
        url={videoCallUrl}
        onUserLeave={closeCall}
        onTrackMediaStream={trackMediaStream}
        onMeetingJoined={() => {
          // Additional logic when meeting is joined
          console.log("Meeting joined successfully");
          
        }}
      />
    );
  }

  // Rest of your existing render code for the calling UI
  return (
    <div
      className="absolute inset-0 w-full h-full bg-slate-900/50 backdrop-blur-md flex flex-col justify-center items-center animate-fadeIn overflow-hidden"
      style={{ zIndex: 50 }}
    >
      <audio
        ref={callToneAudioRef}
        src="https://storage.googleapis.com/ai-companion-81b04.firebasestorage.app/audio/call_tone_1.mp3"
        loop
        autoPlay
        style={{ display: "none" }}
      />
      {/* Enhanced styles matching the ErotiX UI */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes softPulse {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 1; }
          }
          
          @keyframes dotPulse {
            0%, 100% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.2); opacity: 1; }
          }
          
          @keyframes subtleGlow {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.4)); }
            50% { filter: drop-shadow(0 0 12px rgba(220, 38, 38, 0.6)); }
          }
          
          @keyframes heartbeatGlow {
            0%, 100% { box-shadow: 0 0 10px rgba(220, 38, 38, 0.4); }
            50% { box-shadow: 0 0 25px rgba(185, 28, 28, 0.7); }
          }
          
          @keyframes slowSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes starTwinkle {
            0%, 100% { opacity: 0.1; }
            50% { opacity: 0.6; }
          }
          
          @keyframes shimmer {
            0% { background-position: -100% 0; }
            100% { background-position: 200% 0; }
          }
          
          .mode-card {
            background: linear-gradient(to bottom, rgba(15, 23, 42, 0.8), rgba(112, 26, 117, 0.5), rgba(69, 10, 10, 0.4));
            border: 1px solid rgba(185, 28, 28, 0.3);
            border-radius: 16px;
            padding: 24px;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 190px;
            transition: all 0.3s ease;
            cursor: pointer;
            position: relative;
            overflow: hidden;
          }
          
          .mode-card:hover {
            transform: translateY(-5px);
            border-color: rgba(185, 28, 28, 0.6);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(185, 28, 28, 0.25);
          }
          
          .mode-card.active {
            border-color: rgba(220, 38, 38, 0.8);
            box-shadow: 0 0 20px rgba(220, 38, 38, 0.5);
          }
          
          .mode-card:before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 50%;
            height: 100%;
            background: linear-gradient(
              to right,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.05) 50%,
              rgba(255, 255, 255, 0) 100%
            );
            transform: skewX(-25deg);
            transition: all 0.8s ease;
          }
          
          .mode-card:hover:before {
            left: 150%;
            transition: all 0.8s ease;
          }
          
          .shimmer-text {
            background: linear-gradient(to right, #dc2626 10%, #991b1b 30%, #7f1d1d 40%, #450a0a 50%, #7f1d1d 60%, #991b1b 70%, #dc2626 90%);
            background-size: 200% auto;
            background-clip: text;
            text-fill-color: transparent;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: shimmer 2.5s linear infinite;
          }
          
          .button-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(185, 28, 28, 0.3);
          }
          
          .gradient-border {
            position: relative;
            border-radius: 0.75rem;
            background: linear-gradient(to bottom, #0f1629, #0f0d1a);
            z-index: 0;
          }
          
          .gradient-border::before {
            content: "";
            position: absolute;
            top: -1px;
            left: -1px;
            right: -1px;
            bottom: -1px;
            border-radius: 0.85rem;
            background: linear-gradient(45deg, #7f1d1d, #450a0a, #991b1b, #450a0a);
            z-index: -1;
            opacity: 0.5;
            transition: opacity 0.3s ease;
          }
          
          .gradient-border:hover::before {
            opacity: 0.8;
          }
        `}
      </style>
      {/* Background subtle glow effect */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-purple-900/90 via-fuchsia-900/80 to-red-900/70">
        {/* Background stars */}
        {Array.from({ length: 100 }, (_, i) => (
          <div
            key={`star-${i}`}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              background: "white",
              borderRadius: "50%",
              opacity: 0.4,
              animation: `starTwinkle ${Math.random() * 3 + 2}s infinite ease-in-out alternate`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center" style={{ zIndex: 51 }}>
        {/* Avatar container with orbital ring */}
        <div
          style={{
            position: "relative",
            width: "180px",
            height: "180px",
            marginBottom: "40px",
          }}
        >
          {/* Orbital ring */}
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: "1px solid rgba(220, 38, 38, 0.3)",
              animation: "slowSpin 12s linear infinite",
            }}
          >
            {/* Small heart on the orbital ring */}
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "50%",
                transform: "translate(-50%, -5px)",
                width: "10px",
                height: "10px",
                animation: "subtleGlow 2s ease infinite",
                color: "#ef4444",
              }}
            >
              <IconHeart />
            </div>
          </div>

          {/* Main avatar circle */}
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              width: "160px",
              height: "160px",
              borderRadius: "50%",
              background: "linear-gradient(45deg, #7f1d1d, #b91c1c)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0 0 20px rgba(220, 38, 38, 0.3)",
              animation: "heartbeatGlow 2s ease infinite",
            }}
          >
            <div
              style={{
                width: "152px",
                height: "152px",
                borderRadius: "50%",
                background: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "70px",
                fontWeight: "300",
                color: "white",
                letterSpacing: "2px",
              }}
            >
              E
            </div>
          </div>
        </div>

        {/* Mode label */}
        <div
          style={{
            fontSize: "16px",
            textTransform: "uppercase",
            letterSpacing: "2px",
            fontWeight: "600",
            color: selectedMode === "erotix" ? "#f43f5e" : "#f9a8d4",
            marginBottom: "8px",
          }}
        >
          {selectedMode === "erotix" ? "ErotiX Mode" : "Companion Mode"}
        </div>

        {/* Calling text */}
        <div
          style={{
            fontSize: "24px",
            fontWeight: "300",
            letterSpacing: "2px",
            margin: "0 0 20px 0",
            textAlign: "center",
            animation: "softPulse 2s infinite",
            color: "rgba(255, 255, 255, 0.9)",
          }}
        >
          Video Calling Eve...
        </div>

        {/* Pulsing dots */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "40px",
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#dc2626",
                animation: `dotPulse 1.5s infinite ${i * 0.3}s`,
              }}
            />
          ))}
        </div>

        {/* Cancel button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("Cancel button clicked");
            // Directly clean up media first
            cleanupMediaResources();
            // Then close the call through our handler
            closeCall();
          }}
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(220, 38, 38, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "white",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(220, 38, 38, 0.2)";
            e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
            e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.3)";
          }}
        >
          <IconClose />
        </button>
      </div>
    </div>
  );
};

export default ChatVideoCallComponent;
